import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  listCatalogPrefix,
  listCatalogRows,
  listFetchErrorKey,
  listFetchStateKey,
  listRowKey,
  listRows,
  listValues,
} from "../../../shared/connectors/listRows";
import { configurationPatch } from "../../../shared/connectors/configurationPatch";
import type { ListField } from "../../../shared/connectors/types";
import { visitorGrantReady } from "../../../shared/connectors/visitorGrantReady";
import { sharedMutators } from "../../../shared/mutators";
import { useMyGrantForConnector } from "../../hooks/useMyConnectorGrants";
import { withConnectorDestinationProof } from "../../sealSecret";
import { Box } from "../Box";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { InlineInputButton } from "../InlineInput";
import { Input } from "../Input";
import { ListTable } from "../ListTable";
import { SearchContainer, SearchInput } from "../SearchField";
import { HoverActions, ListBox } from "../Settings/shared";
import { SettingsCard, SettingsHeading, SettingsRow } from "../SettingsCard";
import { Text } from "../Text";
import { ToggleVisual } from "../Toggle";
import type { App } from "./Form";
import { AllowListPicker } from "./AllowListPicker";
import { PickerRow } from "./PickerRow";

const InputRow = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 8px;
`;

const FetchLink = styled(ClickButton)`
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.3;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const AddButton = styled(InlineInputButton)`
  background: var(--alpha4);
  &:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

const EmptyRow = styled("div")`
  padding: 12px;
`;

type Props = {
  item: ListField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function ResourceListField({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const visitor = app.authorizationMode === "visitor";
  const [callerGrant, callerGrantResult] = useMyGrantForConnector(app.id, {
    enabled: visitor,
  });
  const authorized = visitor
    ? visitorGrantReady(app.oauthRequestedScopes ?? [], callerGrant)
    : (app.configuration.authMode || "oauth") !== "oauth" ||
      app.oauthConnection?.status === "connected";
  const authorizing =
    !visitor && app.oauthConnection?.status === "obtained_code";
  const authorizeHint =
    disabled || authorized || authorizing
      ? null
      : !visitor
        ? "Authorize with the MCP server to load its tools."
        : callerGrantResult.type === "complete"
          ? "Connect this connector with your own account to load its tools."
          : null;
  const column = item.columns[0]!;
  const rows = listRows(app.configuration, item.prefix);
  const catalog = listCatalogRows(app.configuration, item.prefix);
  const allowed = new Set(listValues(app.configuration, item.prefix));
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Picking several models in quick succession fires writes before Zero pushes a
  // re-render, so each write must build on the previous one's result rather than
  // the render-time snapshot — otherwise two picks reuse the same nextId and the
  // second clobbers the first. Advance this on every write, and adopt the prop
  // again once Zero commits it.
  const configRef = useRef(app.configuration);
  useLayoutEffect(() => {
    configRef.current = app.configuration;
  }, [app.configuration]);

  // A list has no native validity, so anchor the "at least one row" rule on the
  // add-input, which the modal's reportValidity() gate already flushes on save.
  useEffect(() => {
    inputRef.current?.setCustomValidity(
      item.required && rows.length === 0 ? "Add at least one entry." : ""
    );
  }, [item.required, rows.length]);

  const writeConfiguration = async (configuration: Record<string, string>) => {
    const before = configRef.current;
    configRef.current = configuration;
    onEdit?.();
    z.mutate(
      sharedMutators.updateConnectedApp(
        await withConnectorDestinationProof(
          { ...app, configuration: before },
          { configurationPatch: configurationPatch(before, configuration) }
        )
      )
    );
  };

  const addValue = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;
    const config = configRef.current;
    if (listValues(config, item.prefix).includes(value)) return false;
    const currentRows = listRows(config, item.prefix);
    const nextId = currentRows.length
      ? Math.max(...currentRows.map((row) => row.id)) + 1
      : 0;
    writeConfiguration({
      ...config,
      [listRowKey(item.prefix, nextId, column.field)]: value,
    });
    return true;
  };

  const tryAdd = () => {
    if (addValue(draft)) setDraft("");
  };

  const removeRowKeys = (rowPrefix: string) =>
    writeConfiguration(
      Object.fromEntries(
        Object.entries(configRef.current).filter(
          ([key]) => !key.startsWith(rowPrefix)
        )
      )
    );

  const removeRow = (id: number) => removeRowKeys(`${item.prefix}.${id}.`);

  const toggleValue = (name: string) => {
    const config = configRef.current;
    if (listValues(config, item.prefix).includes(name)) {
      const row = listRows(config, item.prefix).find(
        (r) => r.values[column.field]?.trim() === name
      );
      if (row) removeRow(row.id);
    } else {
      addValue(name);
    }
  };

  const clearCatalog = () => {
    const catalogPrefix = `${listCatalogPrefix(item.prefix)}.`;
    setSearch("");
    writeConfiguration(
      Object.fromEntries(
        Object.entries(configRef.current).filter(
          ([key]) => !key.startsWith(catalogPrefix)
        )
      )
    );
  };

  const fetchState = app.configuration[listFetchStateKey(item.prefix)];
  const fetching = fetchState === "fetching";
  const fetchError =
    fetchState === "error"
      ? app.configuration[listFetchErrorKey(item.prefix)]
      : null;
  const isMcp = item.fetch?.protocol === "mcp";
  const toolFilterEnabled = app.configuration.toolFilter === "enabled";

  const refresh = () =>
    z.mutate(
      sharedMutators.refreshConnectorList({
        connectedAppId: app.id,
        prefix: item.prefix,
      })
    );

  const shouldFetch = isMcp && toolFilterEnabled && !disabled && authorized;
  const autoFetched = useRef(false);
  useEffect(() => {
    if (!shouldFetch) {
      autoFetched.current = false;
      return;
    }
    if (autoFetched.current) return;
    autoFetched.current = true;
    if (!fetching) refresh();
  });

  const needle = search.trim().toLowerCase();
  const filteredCatalog = needle
    ? catalog.filter((row) =>
        (row.values[column.field] ?? "").toLowerCase().includes(needle)
      )
    : catalog;

  if (isMcp) {
    return (
      <SettingsCard
        role="region"
        aria-label="Tool access"
        style={{ marginTop: 16 }}
      >
        <SettingsRow>
          <SettingsHeading>
            <Text variant="text-regular-md">Limit tool access</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {!toolFilterEnabled && authorizeHint
                ? authorizeHint
                : "When on, only selected tools are allowed. When off, all tools are allowed, including new ones."}
            </Text>
          </SettingsHeading>
          <ClickButton
            type="button"
            role="switch"
            aria-label="Limit tool access"
            aria-checked={toolFilterEnabled}
            disabled={
              disabled || (!toolFilterEnabled && !authorized && !authorizing)
            }
            style={{ flexShrink: 0 }}
            onClick={() =>
              writeConfiguration({
                ...configRef.current,
                toolFilter: toolFilterEnabled ? "all" : "enabled",
              })
            }
          >
            <ToggleVisual isOn={toolFilterEnabled} />
          </ClickButton>
        </SettingsRow>
        {toolFilterEnabled && (
          <AllowListPicker
            items={catalog.map((row) => ({
              name: row.values[column.field] ?? "",
              title: row.values.title,
              description: row.values.description,
            }))}
            noun={{ singular: "tool", plural: "tools" }}
            icon="tool-case"
            selected={allowed}
            disabled={disabled}
            error={authorized ? fetchError : authorizeHint}
            onToggle={toggleValue}
            refresh={{
              fetching: fetching || authorizing,
              onRefresh: authorized
                ? () => {
                    onEdit?.();
                    refresh();
                  }
                : undefined,
            }}
          />
        )}
      </SettingsCard>
    );
  }

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>

      {!disabled && (
        <InputRow>
          <Input
            ref={inputRef}
            type="text"
            placeholder={
              column.placeholder
                ? `${item.addLabel} (e.g. ${column.placeholder})`
                : item.addLabel
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                tryAdd();
              }
            }}
            style={{ width: "100%", paddingRight: 36 }}
          />
          <AddButton
            type="button"
            disabled={!draft.trim()}
            aria-label={item.addLabel}
            onClick={tryAdd}
          >
            <Icon name="plus" size={16} />
          </AddButton>
        </InputRow>
      )}

      <HelperRow>
        <Text
          variant="text-small"
          color={fetchError ? "var(--red-600)" : "var(--text-secondary)"}
        >
          {fetchError ?? item.helperText}
        </Text>
        {item.fetch && !disabled && (
          <FetchLink
            disabled={fetching}
            onClick={() => {
              onEdit?.();
              refresh();
            }}
          >
            {fetching ? "Fetching…" : item.fetch.label}
          </FetchLink>
        )}
      </HelperRow>

      {catalog.length > 0 && !disabled && (
        <Box top={8}>
          <ListBox style={{ flex: "none", overflow: "hidden" }}>
            <SearchContainer>
              <Icon name="search" size={16} />
              <SearchInput
                type="text"
                placeholder={`Search ${catalog.length}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <ClickButton
                type="button"
                aria-label="Dismiss fetched results"
                onClick={clearCatalog}
              >
                <Icon name="x" size={16} />
              </ClickButton>
            </SearchContainer>
            <div style={{ maxHeight: 280, overflow: "auto" }}>
              {filteredCatalog.map((row) => {
                const name = row.values[column.field] ?? "";
                const checked = allowed.has(name);
                return (
                  <PickerRow
                    key={row.id}
                    data-checked={checked}
                    onClick={() => toggleValue(name)}
                  >
                    <Checkbox checked={checked} />
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {name}
                    </Text>
                  </PickerRow>
                );
              })}
              {filteredCatalog.length === 0 && (
                <EmptyRow>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Nothing matches “{search}”.
                  </Text>
                </EmptyRow>
              )}
            </div>
          </ListBox>
        </Box>
      )}

      {rows.length > 0 && (
        <Box top={8}>
          <ListBox
            data-compact="true"
            style={{ flex: "none", maxHeight: 320, overflow: "auto" }}
          >
            <ListTable
              hideHeaders
              headers={{ name: "", actions: "" }}
              columns={{ name: "flex" }}
              items={rows.map((row) => ({
                key: String(row.id),
                name: (
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {row.values[column.field]}
                  </Text>
                ),
                actions: (hasMouse: boolean) =>
                  disabled ? null : (
                    <HoverActions visible={hasMouse}>
                      <Button
                        type="button"
                        variant="secondary"
                        icon="x"
                        onClick={() => removeRow(row.id)}
                      />
                    </HoverActions>
                  ),
              }))}
            />
          </ListBox>
        </Box>
      )}
    </div>
  );
}
